import { useState } from "react";
import { InputCenterCost } from "../../components/cards/InputCenterCost";
import { InputEmployeeByCenterCost } from "../../components/cards/InputEmployeeByCenterCost";
import SelectDateGetDataEmployee from "../../components/cards/SelectDateGetDataEmployee";
import axios from "axios";
import useStore from "../../store/useStore";
import { format } from "date-fns";
import { toast } from "sonner";
import { MapPontoResidencia } from "../../components/MapPontoResidencia/MapPontoResidencia";
// Importar ícones do Phosphor
import { XCircle, PlusMinus, ThumbsUp } from "@phosphor-icons/react";

interface PontoData {
  matricula: string;
  nome: string;
  cidade: string;
  endereco: string;
  residencia?: {
    latitude: number;
    longitude: number;
  };
  batidas?: {
    entrada1?: {
      hora: string;
      latitude: number;
      longitude: number;
    };
    saida1?: {
      hora: string;
      latitude: number;
      longitude: number;
    };
    entrada2?: {
      hora: string;
      latitude: number;
      longitude: number;
    };
    saida2?: {
      hora: string;
      latitude: number;
      longitude: number;
    };
  };
}

export function PontoResidencia() {
  const [isLoading, setIsLoading] = useState(false);
  const [pontoData, setPontoData] = useState<PontoData | undefined>(undefined);
  const { selectedEmployee, trackingDate } = useStore((state) => state);

  const isDisabled = !selectedEmployee || !trackingDate;

  // Componentes de toast personalizados com ícones
  const ToastErro = ({ children }: { children: React.ReactNode }) => (
    <div className="flex items-center gap-2">
      <XCircle size={32} className="text-red-500" />
      <span>{children}</span>
    </div>
  );

  const ToastEntradaSaida = ({ children }: { children: React.ReactNode }) => (
    <div className="flex items-center gap-2">
      <PlusMinus size={32} className="text-amber-500" />
      <span>{children}</span>
    </div>
  );

  const ToastCompleto = ({ children }: { children: React.ReactNode }) => (
    <div className="flex items-center gap-2">
      <ThumbsUp size={32} className="text-green-500" />
      <span>{children}</span>
    </div>
  );

  async function handleOnclick() {
    if (isLoading) return;

    if (!selectedEmployee || !trackingDate) {
      toast(<ToastErro>Selecione um funcionário e uma data para continuar</ToastErro>);
      return;
    }

    if (!selectedEmployee.register || !selectedEmployee.branch) {
      toast(<ToastErro>Dados do funcionário incompletos</ToastErro>);
      return;
    }

    setIsLoading(true);
    try {
      const formattedDate = format(trackingDate, "yyyy-MM-dd");
      const { data } = await axios.get<PontoData>(
        "https://radar.tlpservicos.com.br/api/dp/ponto-posicao",
        {
          params: {
            matricula: selectedEmployee.register,
            filial: selectedEmployee.branch,
            Date: formattedDate,
          },
        }
      );

      setPontoData(data);

      // Verificar se há batidas
      if (!data.batidas) {
        toast(<ToastErro>Não há batidas para esse dia ainda</ToastErro>);
      } else {
        // Verificar se há batidas de entrada
        const temEntrada = !!(data.batidas.entrada1 || data.batidas.entrada2);

        // Verificar se há batidas de saída
        const temSaida = !!(data.batidas.saida1 || data.batidas.saida2);

        // Mostrar toasts específicos
        if (temEntrada && temSaida) {
          toast(<ToastCompleto>Há batidas de entrada e saída</ToastCompleto>);
        } else if (temEntrada) {
          toast(<ToastEntradaSaida>Há batidas somente de entrada</ToastEntradaSaida>);
        } else if (temSaida) {
          toast(<ToastEntradaSaida>Há batidas somente de saída</ToastEntradaSaida>);
        }

        if (!temEntrada && !temSaida) {
          toast(<ToastErro>Não há batidas para esse dia ainda</ToastErro>);
        }
      }
    } catch (error) {
      setPontoData(undefined);
      toast.error(
        <ToastErro>
          Erro ao carregar dados do ponto
          <p className="text-sm mt-1">Tente novamente mais tarde</p>
        </ToastErro>
      );
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="w-full h-screen grid grid-cols-4 gap-4">
      <div className="flex flex-col">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 h-16">
            <InputCenterCost />
          </div>
          <div className="col-span-2 h-16">
            <InputEmployeeByCenterCost />
          </div>
          <div className="col-span-2 h-16">
            <SelectDateGetDataEmployee />
          </div>
          <div className="col-span-2">
            <button
              disabled={isDisabled || isLoading}
              onClick={handleOnclick}
              type="submit"
              className="w-full border-2 border-blue-300 text-blue-300 text-md font-bold py-2 px-4 
                        rounded-md hover:bg-blue-300 hover:text-gray-500 transition duration-300 
                        tracking-wider uppercase disabled:cursor-not-allowed disabled:opacity-50
                        relative"
            >
              {isLoading ? (
                <>
                  <span className="opacity-0">gerar pontos</span>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-300"></div>
                  </div>
                </>
              ) : (
                "gerar pontos"
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="h-[900px] col-span-3">
        <MapPontoResidencia pontoData={pontoData} />
      </div>
    </div>
  );
}
